/**
 * Kachel-Schicht (D-07, BE-06) - rein und getestet.
 *
 * Die Übersicht und jede Bereichsseite bestehen aus Kacheln in Spalten. Welche
 * Kacheln es gibt, steht im Katalog; wie sie angeordnet sind, ist ein kleines
 * Layout je Seite (`uebersicht`, `bereich:<id>`), das synchronisiert wird.
 * Ohne gespeichertes Layout gilt der Standard - also genau die bisherige
 * Anordnung.
 *
 * Das Layout hat immer drei Spalten (Laptop). Auf schmaleren Bildschirmen
 * werden die Spalten der Reihe nach zusammengefasst (`spaltenFuer`): zwei
 * Spalten auf kleinen Laptops, eine am Handy (Leitlinie 1).
 */

import type { EigeneStatistik } from '@/domain/oberflaeche/statistik';
import type { Sparziel } from '@/domain/finanzen/sparziele';
import type { Zaehler } from '@/domain/oberflaeche/zaehler';
import type { FesterBereich } from '@/domain/types';
import type { SeitenleistenEinstellung } from '@/domain/oberflaeche/seitenleiste';

/** Eine Zeile der Tabelle `oberflaeche` (N-01, D-07): je Einstellung eine. */
export type OberflaecheZeile = SeitenleistenEinstellung | KachelLayout | Zaehler | Sparziel | EigeneStatistik;

export type KachelScope = 'uebersicht' | 'bereich';

export interface KachelTyp {
  id: string;
  titel: string;
  beschreibung: string;
  scope: KachelScope;
  /** Nur auf der Seite dieses festen Bereichs (BE-03). */
  fuer?: FesterBereich;
}

/** Alle Kacheln, die es gibt (die mit `fuer` nur auf der Seite des jeweiligen festen Bereichs). Die Darstellung steht in `modules/kacheln/katalog.tsx`. */
export const KACHEL_TYPEN: readonly KachelTyp[] = [
  { id: 'tageskennzahlen', titel: 'Heute in Zahlen', beschreibung: 'Aufgaben, Gewohnheiten, Training der Woche und Lernzeit', scope: 'uebersicht' },
  { id: 'zeit-fortschritt', titel: 'Zeit-Fortschritt', beschreibung: 'Woche, Monat, Jahr und Schulhalbjahr als Balken', scope: 'uebersicht' },
  { id: 'ausgaben', titel: 'Ausgaben diesen Monat', beschreibung: 'Ausgegeben von Budget, größte Kategorien, Tagesbudget', scope: 'uebersicht' },
  { id: 'heute', titel: 'Heute', beschreibung: 'Aufgaben für heute und der Fokus der Woche', scope: 'uebersicht' },
  { id: 'ueberfaellig', titel: 'Überfällig', beschreibung: 'Was liegen geblieben ist', scope: 'uebersicht' },
  { id: 'gewohnheiten-heute', titel: 'Gewohnheiten heute', beschreibung: 'Heute abhaken, mit Serie', scope: 'uebersicht' },
  { id: 'kalender', titel: 'Kalender', beschreibung: 'Agenda von heute mit freien Fenstern, dazu die nächsten Tage', scope: 'uebersicht' },
  { id: 'zeitverteilung', titel: 'Zeitverteilung', beschreibung: 'Wohin die Zeit geht, nach Bereichen', scope: 'uebersicht' },
  { id: 'bereiche', titel: 'Bereiche', beschreibung: 'Status aller Bereiche mit dem Lebensrad', scope: 'uebersicht' },
  { id: 'zukunft', titel: 'Blick in die Zukunft', beschreibung: 'Termine, Deadlines, Projekte und Ziele der nächsten Tage', scope: 'uebersicht' },

  { id: 'b-status', titel: 'Status', beschreibung: 'Lebensrad, offene Aufgaben, Projekte, Zielfortschritt, Zeit', scope: 'bereich' },
  { id: 'b-training', titel: 'Training', beschreibung: 'Wochenfortschritt des Trainings', scope: 'bereich' },
  { id: 'b-ernaehrung', titel: 'Ernährung', beschreibung: 'Nährwerte und Trinken heute', scope: 'bereich' },
  { id: 'b-aufgaben', titel: 'Aufgaben', beschreibung: 'Offene Aufgaben dieses Bereichs, direkt anlegen', scope: 'bereich' },
  { id: 'b-gewohnheiten', titel: 'Gewohnheiten', beschreibung: 'Heute abhaken, Serie und Quote', scope: 'bereich' },
  { id: 'b-ziele', titel: 'Ziele', beschreibung: 'Ziele des Bereichs mit Fortschritt', scope: 'bereich' },
  { id: 'b-projekte', titel: 'Projekte', beschreibung: 'Projekte des Bereichs mit Fortschritt', scope: 'bereich' },
  { id: 'b-deadlines', titel: 'Deadlines', beschreibung: 'Anstehende Fristen', scope: 'bereich' },
  { id: 'b-termine', titel: 'Termine (7 Tage)', beschreibung: 'Termine der nächsten sieben Tage', scope: 'bereich' },
  { id: 'b-statistik', titel: 'Statistik', beschreibung: 'Erledigte Aufgaben je Woche, Gewohnheiten, Lebensrad', scope: 'bereich' },
  { id: 'b-wissen', titel: 'Wissen', beschreibung: 'Verknüpfte Wissensquellen', scope: 'bereich' },
  { id: 'b-notizen', titel: 'Notizen & Gedanken', beschreibung: 'Verknüpfte Notizen und Gedanken', scope: 'bereich' },
  { id: 'b-listen', titel: 'Listen', beschreibung: 'Listen dieses Bereichs', scope: 'bereich' },

  // Nur "Gesundheit & Körper" (BE-03)
  { id: 'g-wochenplan', titel: 'Wochenplan', beschreibung: 'Mo–So mit Status je Tag', scope: 'bereich', fuer: 'gesundheit' },
  { id: 'g-muskeln', titel: 'Muskel-Abdeckung', beschreibung: 'Wie gut jeder Muskel trainiert wurde, Regeneration und Stand', scope: 'bereich', fuer: 'gesundheit' },
  { id: 'g-naehrwerte', titel: 'Nährwerte heute', beschreibung: 'Energie, Eiweiß, Kohlenhydrate, Fett und Trinken gegen das Ziel', scope: 'bereich', fuer: 'gesundheit' },

  // Nur "Schule" (BE-04)
  { id: 's-heute', titel: 'Heute in der Schule', beschreibung: 'Stunden von heute, Ferien und Countdown zur nächsten Klausur', scope: 'bereich', fuer: 'schule' },
  { id: 's-stundenplan', titel: 'Stundenplan', beschreibung: 'Der Stundenplan der aktuellen A- bzw. B-Woche', scope: 'bereich', fuer: 'schule' },
  { id: 's-noten', titel: 'Noten', beschreibung: 'Schnitt je Fach und Noten eintragen', scope: 'bereich', fuer: 'schule' },
  { id: 's-faecher', titel: 'Fächer', beschreibung: 'Fächer als Karten mit Schnitt, Hausaufgaben und Stunden', scope: 'bereich', fuer: 'schule' },
  { id: 's-verlauf', titel: 'Notenverlauf', beschreibung: 'Noten je Fach seit Beginn des Schuljahres', scope: 'bereich', fuer: 'schule' },
  { id: 's-hausaufgaben', titel: 'Hausaufgaben', beschreibung: 'Offene Hausaufgaben nach Fälligkeit', scope: 'bereich', fuer: 'schule' },
  { id: 's-klausuren', titel: 'Klausuren', beschreibung: 'Anstehende Klausuren mit Lernplan', scope: 'bereich', fuer: 'schule' },
  { id: 's-lernzeit', titel: 'Lernzeit', beschreibung: 'Stoppuhr, Wochendiagramm und Lernzeit je Fach', scope: 'bereich', fuer: 'schule' },

  // Nur "Finanzen" (BE-05)
  { id: 'f-budget', titel: 'Budget nach Kategorie', beschreibung: 'Ausgaben je Kategorie gegen das Budget, mit Hochrechnung', scope: 'bereich', fuer: 'finanzen' },
  { id: 'f-verlauf', titel: 'Einnahmen und Ausgaben', beschreibung: 'Die letzten 6 Monate als Säulen', scope: 'bereich', fuer: 'finanzen' },
  { id: 'f-sparziele', titel: 'Sparziele', beschreibung: 'Fortschritt und voraussichtliches Erreichungsdatum', scope: 'bereich', fuer: 'finanzen' },
  { id: 'f-abos', titel: 'Abos und Fristen', beschreibung: 'Nächste Abbuchungen und ablaufende Kündigungsfristen', scope: 'bereich', fuer: 'finanzen' },
  { id: 'f-buchungen', titel: 'Letzte Buchungen', beschreibung: 'Die sechs neuesten Buchungen', scope: 'bereich', fuer: 'finanzen' },
];

/** Eine Kachel im Layout. `id` ist je Layout eindeutig (bei mehrfach genutzten Typen mit Zähler). */
export interface Platz { id: string; typ: string }

export interface KachelLayout {
  id: string;
  /** Immer drei Spalten (Laptop). */
  spalten: Platz[][];
  updatedAt: string;
}

const STANDARD_UEBERSICHT: readonly (readonly string[])[] = [
  ['tageskennzahlen', 'heute', 'ueberfaellig', 'gewohnheiten-heute'],
  ['zeit-fortschritt', 'kalender', 'zeitverteilung'],
  ['bereiche', 'ausgaben', 'zukunft'],
];

const STANDARD_BEREICH: readonly (readonly string[])[] = [
  ['b-status', 'b-training', 'b-ernaehrung', 'b-aufgaben', 'b-gewohnheiten'],
  ['b-ziele', 'b-projekte', 'b-deadlines'],
  ['b-termine', 'b-statistik', 'b-wissen', 'b-notizen', 'b-listen'],
];

const STANDARD_GESUNDHEIT: readonly (readonly string[])[] = [
  ['g-wochenplan', 'g-naehrwerte', 'g-muskeln'],
  ['b-aufgaben', 'b-gewohnheiten', 'b-ziele'],
  ['b-termine', 'b-statistik', 'b-notizen', 'b-wissen'],
];

const STANDARD_SCHULE: readonly (readonly string[])[] = [
  ['s-stundenplan', 's-noten'],
  ['s-heute', 's-faecher', 's-hausaufgaben', 's-klausuren'],
  ['s-verlauf', 's-lernzeit', 'b-termine', 'b-notizen', 'b-wissen'],
];

const STANDARD_FINANZEN: readonly (readonly string[])[] = [
  ['f-budget', 'f-buchungen'],
  ['f-verlauf', 'f-sparziele', 'f-abos', 'b-aufgaben'],
  ['b-termine', 'b-statistik', 'b-notizen', 'b-wissen'],
];

export const layoutIdBereich = (areaId: string) => `bereich:${areaId}`;
export const scopeVon = (layoutId: string): KachelScope => (layoutId === 'uebersicht' ? 'uebersicht' : 'bereich');

export function standardLayout(layoutId: string, fest?: FesterBereich): KachelLayout {
  const std = scopeVon(layoutId) === 'uebersicht' ? STANDARD_UEBERSICHT : fest === 'gesundheit' ? STANDARD_GESUNDHEIT : fest === 'schule' ? STANDARD_SCHULE : fest === 'finanzen' ? STANDARD_FINANZEN : STANDARD_BEREICH;
  return { id: layoutId, spalten: std.map((s) => s.map((typ) => ({ id: typ, typ }))), updatedAt: '' };
}

/** Eine Seite ohne Kacheln - so startet ein neuer, selbst angelegter Bereich (BE-06). */
export const leeresLayout = (layoutId: string): KachelLayout => ({ id: layoutId, spalten: [[], [], []], updatedAt: '' });

export const layoutIstLeer = (l: KachelLayout): boolean => l.spalten.every((s) => s.length === 0);

const typPasst = (typ: string, scope: KachelScope, fest?: FesterBereich) =>
  KACHEL_TYPEN.some((t) => t.id === typ && t.scope === scope && (!t.fuer || t.fuer === fest));

/**
 * Gespeichertes Layout pruefen (Systemgrenze: kommt aus der Datenbank bzw. vom
 * Sync). Unbekannte Kacheltypen (aus einer neueren Version) bleiben erhalten,
 * damit ein Speichern sie nicht loescht; die Anzeige ueberspringt sie.
 */
export function layoutAus(roh: unknown, layoutId: string, fest?: FesterBereich): KachelLayout {
  if (typeof roh !== 'object' || roh === null) return standardLayout(layoutId, fest);
  const r = roh as Record<string, unknown>;
  if (!Array.isArray(r.spalten)) return standardLayout(layoutId, fest);
  const gesehen = new Set<string>();
  const spalten: Platz[][] = [0, 1, 2].map((i) => {
    const s = (r.spalten as unknown[])[i];
    if (!Array.isArray(s)) return [];
    return s.flatMap((p): Platz[] => {
      if (typeof p !== 'object' || p === null) return [];
      const { id, typ } = p as Record<string, unknown>;
      if (typeof id !== 'string' || typeof typ !== 'string' || gesehen.has(id)) return [];
      gesehen.add(id);
      return [{ id, typ }];
    });
  });
  return { id: layoutId, spalten, updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : '' };
}

const flach = (l: KachelLayout) => l.spalten.flat();
const inSpalten = (l: KachelLayout, plaetze: Platz[]): KachelLayout => {
  let i = 0;
  return { ...l, spalten: l.spalten.map((s) => s.map(() => plaetze[i++]!)) };
};

/** Die Spalten fuer `n` Bildschirmspalten: die gespeicherten drei der Reihe nach zusammengefasst. */
export function spaltenFuer(l: KachelLayout, n: 1 | 2 | 3): Platz[][] {
  if (n === 3) return l.spalten;
  const alle = flach(l);
  const proSpalte = Math.ceil(alle.length / n);
  return Array.from({ length: n }, (_, i) => alle.slice(i * proSpalte, (i + 1) * proSpalte));
}

/** Kacheln, die ins Layout gestellt werden koennen: alle des Scopes, die noch nicht darin stehen. */
export function hinzufuegbar(l: KachelLayout, fest?: FesterBereich): KachelTyp[] {
  const scope = scopeVon(l.id);
  const drin = new Set(flach(l).map((p) => p.typ));
  return KACHEL_TYPEN.filter((t) => typPasst(t.id, scope, fest) && !drin.has(t.id));
}

export type Richtung = 'hoch' | 'runter' | 'links' | 'rechts';

/**
 * Verschiebt eine Kachel. Mit `fluss` (weniger als drei Bildschirmspalten)
 * zaehlt nur die Reihenfolge: hoch und runter laufen ueber die Spalten
 * hinweg; links und rechts gibt es dann nicht.
 */
export function verschiebe(l: KachelLayout, id: string, richtung: Richtung, fluss = false): KachelLayout {
  if (fluss) {
    if (richtung === 'links' || richtung === 'rechts') return l;
    const alle = flach(l);
    const i = alle.findIndex((p) => p.id === id);
    const j = richtung === 'hoch' ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= alle.length) return l;
    [alle[i], alle[j]] = [alle[j]!, alle[i]!];
    return inSpalten(l, alle);
  }
  const s = l.spalten.findIndex((sp) => sp.some((p) => p.id === id));
  if (s < 0) return l;
  const spalten = l.spalten.map((sp) => [...sp]);
  const i = spalten[s]!.findIndex((p) => p.id === id);
  const platz = spalten[s]![i]!;
  if (richtung === 'hoch' || richtung === 'runter') {
    const j = richtung === 'hoch' ? i - 1 : i + 1;
    if (j < 0 || j >= spalten[s]!.length) return l;
    [spalten[s]![i], spalten[s]![j]] = [spalten[s]![j]!, spalten[s]![i]!];
  } else {
    const z = richtung === 'links' ? s - 1 : s + 1;
    if (z < 0 || z > 2) return l;
    spalten[s]!.splice(i, 1);
    // An derselben Hoehe einsortieren, hoechstens am Ende der Zielspalte.
    spalten[z]!.splice(Math.min(i, spalten[z]!.length), 0, platz);
  }
  return { ...l, spalten };
}

export function entferne(l: KachelLayout, id: string): KachelLayout {
  return { ...l, spalten: l.spalten.map((s) => s.filter((p) => p.id !== id)) };
}

/** Stellt einen Platz (z. B. einen eigenen Zaehler) ans Ende der kuerzesten Spalte. */
export function fuegePlatzHinzu(l: KachelLayout, platz: Platz): KachelLayout {
  if (flach(l).some((p) => p.id === platz.id)) return l;
  const kuerzeste = l.spalten.reduce((best, s, i) => (s.length < l.spalten[best]!.length ? i : best), 0);
  return { ...l, spalten: l.spalten.map((s, i) => (i === kuerzeste ? [...s, platz] : s)) };
}

/** Stellt eine Kachel ans Ende der kuerzesten Spalte. */
export function fuegeHinzu(l: KachelLayout, typ: string, fest?: FesterBereich): KachelLayout {
  if (!typPasst(typ, scopeVon(l.id), fest) || flach(l).some((p) => p.typ === typ)) return l;
  const kuerzeste = l.spalten.reduce((best, s, i) => (s.length < l.spalten[best]!.length ? i : best), 0);
  return { ...l, spalten: l.spalten.map((s, i) => (i === kuerzeste ? [...s, { id: typ, typ }] : s)) };
}
